/**
 * Exhibition Map
 * Syncs map pins and the location list. Both sides share the same block id.
 */
(function () {
  'use strict';

  // Root container selector for each section instance.
  // The Liquid template renders: <div class="exhibition-map" data-exhibition-map ...>
  var SELECTOR = '[data-exhibition-map]';

  function ExhibitionMap(root) {
    // `root` is the section wrapper element.
    this.root = root;

    // Pin buttons + list buttons inside this section share the same location ids.
    this.triggers = Array.prototype.slice.call(root.querySelectorAll('[data-location-id]'));

    // Optional list container (only exists when blocks size > 0 in the section).
    this.details = root.querySelector('[data-exhibition-details]');

    // Simple registry for potential future bindings (kept for compatibility).
    this.bound = {};

    // Currently active location id (string).
    this.activeId = this.getActiveId();

    if (this.details) {
      this.setupCustomScrollbar();
    }

    // If there are no triggers, there is nothing to sync.
    if (!this.triggers.length) return;

    this.bind();
  }

  ExhibitionMap.prototype.getActiveId = function () {
    // Prefer an element already marked as active (CSS class: .is-active).
    var active = this.root.querySelector('[data-location-id].is-active');
    if (active) return active.getAttribute('data-location-id');

    // Fallback: use the first trigger if nothing is marked active yet.
    return this.triggers[0] ? this.triggers[0].getAttribute('data-location-id') : '';
  };

  ExhibitionMap.prototype.getScrollbarMetrics = function () {
    var el = this.details;
    var scrollWidth = el.scrollWidth;
    var clientWidth = el.clientWidth;
    var maxScroll = scrollWidth - clientWidth;
    var trackWidth = this.scrollbar.offsetWidth;

    if (!trackWidth) {
      trackWidth = Math.max(this.detailsWrap.clientWidth, 0);
    }

    var thumbWidth = scrollWidth > 0
      ? Math.max((clientWidth / scrollWidth) * trackWidth, 24)
      : 24;
    var maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);

    return {
      maxScroll: maxScroll,
      trackWidth: trackWidth,
      thumbWidth: thumbWidth,
      maxThumbLeft: maxThumbLeft
    };
  };

  ExhibitionMap.prototype.updateCustomScrollbar = function () {
    if (!this.details || !this.detailsWrap || !this.scrollbar || !this.scrollbarThumb) return;

    var el = this.details;

    // Overlay layout can measure as 0 before the map stage height settles.
    if (!el.clientWidth || !this.detailsWrap.clientWidth) return;

    var metrics = this.getScrollbarMetrics();
    var isScrollable = metrics.maxScroll > 1;

    this.detailsWrap.classList.toggle('has-scroll', isScrollable);

    if (!isScrollable) {
      this.scrollbarThumb.style.width = '';
      this.scrollbarThumb.style.transform = '';
      return;
    }

    var thumbLeft = metrics.maxScroll > 0
      ? (el.scrollLeft / metrics.maxScroll) * metrics.maxThumbLeft
      : 0;

    this.scrollbarThumb.style.width = metrics.thumbWidth + 'px';
    this.scrollbarThumb.style.transform = 'translateX(' + thumbLeft + 'px)';
  };

  ExhibitionMap.prototype.startScrollbarDrag = function (clientX) {
    this.scrollbarDragging = true;
    this.scrollbarDragStartX = clientX;
    this.scrollbarDragStartScrollLeft = this.details.scrollLeft;
    this.scrollbarDragMetrics = this.getScrollbarMetrics();
    this.detailsWrap.classList.add('is-scrollbar-dragging');
  };

  ExhibitionMap.prototype.moveScrollbarDrag = function (clientX) {
    if (!this.scrollbarDragging || !this.scrollbarDragMetrics) return;

    var metrics = this.scrollbarDragMetrics;
    var deltaX = clientX - this.scrollbarDragStartX;
    var scrollDelta = metrics.maxThumbLeft > 0
      ? (deltaX / metrics.maxThumbLeft) * metrics.maxScroll
      : 0;

    this.details.scrollLeft = Math.max(
      0,
      Math.min(this.scrollbarDragStartScrollLeft + scrollDelta, metrics.maxScroll)
    );
  };

  ExhibitionMap.prototype.endScrollbarDrag = function () {
    if (!this.scrollbarDragging) return;

    this.scrollbarDragging = false;
    this.scrollbarDragMetrics = null;

    if (this.detailsWrap) {
      this.detailsWrap.classList.remove('is-scrollbar-dragging');
    }
  };

  ExhibitionMap.prototype.jumpScrollbarTo = function (clientX) {
    var metrics = this.getScrollbarMetrics();
    if (metrics.maxScroll <= 0 || metrics.maxThumbLeft <= 0) return;

    var trackRect = this.scrollbar.getBoundingClientRect();
    var clickOffset = clientX - trackRect.left - metrics.thumbWidth / 2;
    var clampedOffset = Math.max(0, Math.min(clickOffset, metrics.maxThumbLeft));

    this.details.scrollLeft = (clampedOffset / metrics.maxThumbLeft) * metrics.maxScroll;
  };

  ExhibitionMap.prototype.bindScrollbarDrag = function () {
    var self = this;

    this.bound.onScrollbarPointerDown = function (event) {
      if (!self.detailsWrap.classList.contains('has-scroll')) return;
      if (event.type === 'mousedown' && event.button !== 0) return;

      var clientX = event.touches ? event.touches[0].clientX : event.clientX;

      if (self.scrollbarThumb.contains(event.target)) {
        self.startScrollbarDrag(clientX);
      } else {
        self.jumpScrollbarTo(clientX);
      }

      event.preventDefault();
    };

    this.bound.onScrollbarPointerMove = function (event) {
      if (!self.scrollbarDragging) return;

      var clientX = event.touches ? event.touches[0].clientX : event.clientX;
      self.moveScrollbarDrag(clientX);
      event.preventDefault();
    };

    this.bound.onScrollbarPointerUp = function () {
      self.endScrollbarDrag();
    };

    this.scrollbar.addEventListener('mousedown', this.bound.onScrollbarPointerDown);
    this.scrollbar.addEventListener('touchstart', this.bound.onScrollbarPointerDown, { passive: false });
    document.addEventListener('mousemove', this.bound.onScrollbarPointerMove);
    document.addEventListener('touchmove', this.bound.onScrollbarPointerMove, { passive: false });
    document.addEventListener('mouseup', this.bound.onScrollbarPointerUp);
    document.addEventListener('touchend', this.bound.onScrollbarPointerUp);
    document.addEventListener('touchcancel', this.bound.onScrollbarPointerUp);
  };

  ExhibitionMap.prototype.scheduleScrollbarUpdate = function () {
    var self = this;
    this.updateCustomScrollbar();

    requestAnimationFrame(function () {
      self.updateCustomScrollbar();
    });

    window.setTimeout(function () {
      self.updateCustomScrollbar();
    }, 100);

    window.setTimeout(function () {
      self.updateCustomScrollbar();
    }, 500);
  };

  ExhibitionMap.prototype.setupCustomScrollbar = function () {
    this.detailsWrap = this.details.closest('.exhibition-map-details-wrap');
    if (!this.detailsWrap) return;

    this.scrollbar = this.detailsWrap.querySelector('.exhibition-map-details-scrollbar');
    this.scrollbarThumb = this.detailsWrap.querySelector('.exhibition-map-details-scrollbar-thumb');
    if (!this.scrollbar || !this.scrollbarThumb) return;

    var self = this;
    this.bound.updateScrollbar = function () {
      self.updateCustomScrollbar();
    };

    this.details.addEventListener('scroll', this.bound.updateScrollbar);
    window.addEventListener('resize', this.bound.updateScrollbar);

    if (typeof ResizeObserver !== 'undefined') {
      this.scrollbarObserver = new ResizeObserver(this.bound.updateScrollbar);
      this.scrollbarObserver.observe(this.details);
      this.scrollbarObserver.observe(this.detailsWrap);
    }

    window.addEventListener('load', this.bound.updateScrollbar);

    this.bindScrollbarDrag();
    this.scheduleScrollbarUpdate();
  };

  ExhibitionMap.prototype.prefersReducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  ExhibitionMap.prototype.scrollDetailIntoView = function (detailEl) {
    if (!this.details || !detailEl) return;

    var container = this.details;
    var padding = 4;
    var containerRect = container.getBoundingClientRect();
    var detailRect = detailEl.getBoundingClientRect();
    var scrollOffset = 0;

    if (detailRect.left < containerRect.left + padding) {
      scrollOffset = detailRect.left - containerRect.left - padding;
    } else if (detailRect.right > containerRect.right - padding) {
      scrollOffset = detailRect.right - containerRect.right + padding;
    }

    if (scrollOffset === 0) return;

    container.scrollBy({
      left: scrollOffset,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  ExhibitionMap.prototype.activate = function (id, options) {
    // Defensive: ignore empty ids.
    if (!id) return;

    // options.scrollIntoView decides whether the corresponding list detail should be scrolled into view.
    var scrollIntoView = options && options.scrollIntoView;
    this.activeId = id;

    // Sync UI state (pin + list item) by toggling `.is-active` and `aria-pressed`.
    this.triggers.forEach(function (element) {
      var active = element.getAttribute('data-location-id') === id;
      element.classList.toggle('is-active', active);
      element.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // When clicking a pin, scroll the matching list item into view within the details panel.
    if (!scrollIntoView || !this.details) return;

    var activeDetail = this.details.querySelector('[data-location-id="' + id + '"]');
    if (!activeDetail) return;

    this.scrollDetailIntoView(activeDetail);
  };

  ExhibitionMap.prototype.focusPinWithoutScroll = function (pin) {
    if (!pin || typeof pin.focus !== 'function') return;

    try {
      pin.focus({ preventScroll: true });
    } catch (err) {
      pin.focus();
    }
  };

  ExhibitionMap.prototype.bind = function () {
    var self = this;

    // Prevent browser "scroll focused control into view" when clicking map pins.
    // Keyboard users are unaffected (Tab still focuses normally).
    this.bound.onPinMouseDown = function (event) {
      var pin = event.target.closest('.exhibition-map-pin');
      if (!pin || !self.root.contains(pin)) return;
      if (event.button !== 0) return;

      event.preventDefault();
      self.focusPinWithoutScroll(pin);
    };

    // Event delegation: one click listener handles both pin clicks and list clicks.
    this.bound.onClick = function (event) {
      var trigger = event.target.closest('[data-location-id]');
      if (!trigger || !self.root.contains(trigger)) return;

      // When clicking a pin (not the list item), scroll the details panel if needed.
      self.activate(trigger.getAttribute('data-location-id'), {
        scrollIntoView: !trigger.classList.contains('exhibition-map-detail')
      });

      if (trigger.classList.contains('exhibition-map-pin')) {
        self.focusPinWithoutScroll(trigger);
      }
    };

    // Bind to the section root to keep the handler scoped.
    this.root.addEventListener('mousedown', this.bound.onPinMouseDown);
    this.root.addEventListener('click', this.bound.onClick);
  };

  ExhibitionMap.prototype.destroy = function () {
    // Remove listener to avoid duplicate handlers when Shopify re-renders sections.
    if (this.bound.onPinMouseDown) {
      this.root.removeEventListener('mousedown', this.bound.onPinMouseDown);
    }

    if (this.bound.onClick) {
      this.root.removeEventListener('click', this.bound.onClick);
    }

    if (this.details && this.bound.updateScrollbar) {
      this.details.removeEventListener('scroll', this.bound.updateScrollbar);
    }

    if (this.bound.updateScrollbar) {
      window.removeEventListener('resize', this.bound.updateScrollbar);
      window.removeEventListener('load', this.bound.updateScrollbar);
    }

    if (this.scrollbarObserver) {
      this.scrollbarObserver.disconnect();
      this.scrollbarObserver = null;
    }

    if (this.scrollbar && this.bound.onScrollbarPointerDown) {
      this.scrollbar.removeEventListener('mousedown', this.bound.onScrollbarPointerDown);
      this.scrollbar.removeEventListener('touchstart', this.bound.onScrollbarPointerDown);
    }

    if (this.bound.onScrollbarPointerMove) {
      document.removeEventListener('mousemove', this.bound.onScrollbarPointerMove);
      document.removeEventListener('touchmove', this.bound.onScrollbarPointerMove);
    }

    if (this.bound.onScrollbarPointerUp) {
      document.removeEventListener('mouseup', this.bound.onScrollbarPointerUp);
      document.removeEventListener('touchend', this.bound.onScrollbarPointerUp);
      document.removeEventListener('touchcancel', this.bound.onScrollbarPointerUp);
    }

    this.endScrollbarDrag();
  };

  ExhibitionMap.prototype.selectBlockEl = function (blockEl) {
    // Used by Shopify's `shopify:block:select` event to activate the matching UI within this section.
    if (!blockEl) return;
    var trigger = blockEl.closest('[data-location-id]') || blockEl.querySelector('[data-location-id]');
    var nextId = trigger ? trigger.getAttribute('data-location-id') : '';
    if (nextId) this.activate(nextId, { scrollIntoView: true });
  };

  function initRoot(root) {
    // Initialize (or re-initialize) one section instance.
    if (!root) return;
    if (root._exhibitionMap) {
      root._exhibitionMap.destroy();
      root._exhibitionMap = null;
    }
    root._exhibitionMap = new ExhibitionMap(root);
  }

  function initAll(scope) {
    // Initialize all section instances within the given scope (document or a loaded section element).
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) {
      initRoot(nodes[i]);
    }
  }

  // Initial load for already-rendered sections.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Shopify theme editor / dynamic section load support.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Shopify theme editor / dynamic section unload support.
  document.addEventListener('shopify:section:unload', function (event) {
    var node = event.target.querySelector(SELECTOR);
    if (node && node._exhibitionMap) {
      node._exhibitionMap.destroy();
      node._exhibitionMap = null;
    }
  });

  // When a block is selected in the theme editor, activate the matching pin/list item.
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(SELECTOR) || event.target.querySelector(SELECTOR);
    if (!root || !root._exhibitionMap) return;
    root._exhibitionMap.selectBlockEl(event.target);
  });
})();
